<script setup lang="ts">
import { computed, ref } from 'vue';
import type { PractitionerEntry, ServiceEntry } from '../api';
import ChosenCard from './ChosenCard.vue';
import DetailPopup from './DetailPopup.vue';
import PatientGroupBadge from './PatientGroupBadge.vue';
import { formatPrice, resolvePriceForDate } from '../price';

export type PractitionerState = 'PractitionerMode' | 'PractitionerChosen' | 'PractitionerNotChosen';

const props = defineProps<{
  state: PractitionerState;
  items: PractitionerEntry[];
  services: ServiceEntry[];
  modelValue: string | null;
  displayPrice: boolean;
  selectedDate?: string | null;
  showPrice?: (price?: string) => boolean;
}>();

const shouldShowPrice = (price?: string) => {
  if (!props.displayPrice) {
    return false;
  }
  if (props.showPrice) {
    return props.showPrice(price);
  }
  return !!price && Number(price) > 0;
};

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void;
  (e: 'update:serviceValue', value: string | null): void;
  (e: 'toggle-display-mode'): void;
}>();

const selectedPractitioner = computed(() => {
  return props.items.find((item) => item.uuid === props.modelValue) || null;
});

const getServicePrice = (user: PractitionerEntry) => {
  const override = user.serviceOverrides?.find((service) => !!service.price || !!service.pricing)
    || user.serviceOverrides?.[0];
  const fallbackService = props.services[0];

  return resolvePriceForDate(
    override?.pricing || fallbackService?.pricing,
    override?.price || fallbackService?.price,
    props.selectedDate
  );
};

const updateModelValue = (user: PractitionerEntry) => {
  emit('update:modelValue', user.uuid);
  emit('update:serviceValue', user.serviceOverrides?.find((service) => !!service.uuid)?.uuid || null);
};

const detailItem = ref<PractitionerEntry | null>(null);

function selectFromPopup() {
  if (detailItem.value) {
    updateModelValue(detailItem.value);
  }
  detailItem.value = null;
}
</script>

<template>
  <div class="mbw-practitioner-header" :class="{ 'mbw-section-with-separator': state !== 'PractitionerMode' }">

    <!-- PractitionerMode: header tylko do podglądu (strona osoby) -->
    <div
      v-if="state === 'PractitionerMode' && selectedPractitioner"
    >
      <div class="mbw-mode-title">
        <span v-if="selectedPractitioner.photo" class="mbw-practitioner-mode-avatar-wrap">
          <img
            class="mbw-practitioner-mode-avatar"
            :src="selectedPractitioner.photo"
            :alt="(selectedPractitioner.name + ' ' + selectedPractitioner.lastname).trim()"
          />
          <PatientGroupBadge :group="selectedPractitioner.patientGroup" />
        </span>{{ selectedPractitioner.name }} {{ selectedPractitioner.lastname }}
      </div>
    </div>

    <!-- PractitionerChosen: skrócona karta z wybranym terapeutą (widok formularza) -->
    <div
      v-if="state === 'PractitionerChosen' && selectedPractitioner"
      class="mbw-practitioner-chosen"
    >
      <ChosenCard
        :first-line="(selectedPractitioner.name + ' ' + selectedPractitioner.lastname).trim()"
        :second-line="shouldShowPrice(getServicePrice(selectedPractitioner)) ? `${formatPrice(+getServicePrice(selectedPractitioner))}` : ''"
        :muted-second-line="shouldShowPrice(getServicePrice(selectedPractitioner))"
        @change-click="emit('toggle-display-mode')"
      >
        <template #icon>
          <div class="mbw-practitioner-chosen-avatar">
            <img
              v-if="selectedPractitioner.photo"
              :src="selectedPractitioner.photo"
              :alt="(selectedPractitioner.name + ' ' + selectedPractitioner.lastname).trim()"
            />
            <span v-else>
              {{ selectedPractitioner.name?.charAt(0).toUpperCase() || '•' }}
            </span>
            <PatientGroupBadge :group="selectedPractitioner.patientGroup" />
          </div>
        </template>
      </ChosenCard>
    </div>

    <!-- PractitionerNotChosen / fallback: kafelki wyboru terapeuty (tryb konfiguracji) -->
    <div v-if="state === 'PractitionerNotChosen'" class="mbw-mobile-overflow-icon">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="22" viewBox="0 0 18 22" fill="none">
        <path d="M16.7501 15.4167C16.7501 16.8312 16.1882 18.1877 15.188 19.1879C14.1878 20.1881 12.8312 20.75 11.4167 20.75" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M14.0834 13.4167V12.75C14.0834 12.3964 13.9429 12.0573 13.6929 11.8072C13.4428 11.5572 13.1037 11.4167 12.7501 11.4167C12.3965 11.4167 12.0573 11.5572 11.8073 11.8072C11.5572 12.0573 11.4167 12.3964 11.4167 12.75" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M11.4167 12.75V12.0833C11.4167 11.7297 11.2762 11.3906 11.0261 11.1405C10.7761 10.8905 10.437 10.75 10.0833 10.75C9.72971 10.75 9.39057 10.8905 9.14053 11.1405C8.89048 11.3906 8.75 11.7297 8.75 12.0833V12.75" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M8.74992 12.4167V8.75002C8.74992 8.3964 8.60945 8.05726 8.3594 7.80721C8.10935 7.55716 7.77021 7.41669 7.41659 7.41669C7.06296 7.41669 6.72383 7.55716 6.47378 7.80721C6.22373 8.05726 6.08325 8.3964 6.08325 8.75002V15.4167" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M14.0833 13.4167C14.0833 13.0631 14.2237 12.7239 14.4738 12.4739C14.7238 12.2238 15.063 12.0833 15.4166 12.0833C15.7702 12.0833 16.1094 12.2238 16.3594 12.4739C16.6095 12.7239 16.7499 13.0631 16.7499 13.4167V15.4167C16.7499 16.8312 16.188 18.1877 15.1878 19.1879C14.1876 20.1881 12.8311 20.75 11.4166 20.75H10.0832C8.21658 20.75 7.08325 20.1767 6.08991 19.19L3.68991 16.79C3.46053 16.536 3.33763 16.2035 3.34666 15.8613C3.35568 15.5192 3.49594 15.1936 3.73839 14.952C3.98084 14.7104 4.30691 14.5713 4.64909 14.5635C4.99127 14.5557 5.32335 14.6797 5.57658 14.91L6.74991 16.0833" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M11.4167 0.75L14.0834 3.41667L11.4167 6.08334" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M0.75 3.41669H14.0834" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M3.41667 0.75L0.75 3.41667L3.41667 6.08334" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
    <div v-if="state === 'PractitionerNotChosen'" class="mbw-practitioner-list mbw-mobile-overflow">
      <button
        v-for="item in items"
        :key="item.uuid"
        type="button"
        class="mbw-practitioner-card"
        :class="{ 'mbw-practitioner-card--active': modelValue === item.uuid }"
        @click="updateModelValue(item)"
      >
        <div class="mbw-practitioner-card-avatar">
          <img v-if="item.photo" :src="item.photo" :alt="item.name" :width="40" />
          <span v-else class="mbw-practitioner-card-avatar-fallback">
            {{ item.name?.charAt(0).toUpperCase() || '•' }}
          </span>
          <PatientGroupBadge :group="item.patientGroup" />
          <div v-if="modelValue === item.uuid" class="mbw-practitioner-card-active-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="12" fill="#017BC4"/>
              <path d="M6 12L10 16L18 8" stroke="white" stroke-width="2"/>
            </svg>
          </div>
        </div>
        <div class="mbw-practitioner-card-name">
          {{ item.name.charAt(0).toUpperCase() + item.name.slice(1).toLowerCase() }} {{ item.lastname.charAt(0).toUpperCase() + item.lastname.slice(1).toLowerCase() }}
        </div>
        <div class="mbw-practitioner-card-price" v-if="shouldShowPrice(getServicePrice(item))">
           {{ formatPrice(+getServicePrice(item)) }}
        </div>
        <a href="javascript:void(0)"
          class="mbw-practitioner-card-more"
          @click.prevent.stop="detailItem = item"
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
            <span class="mbw-practitioner-card-more-text">Więcej</span>
        </a>
      </button>
    </div>

    <DetailPopup
      :visible="!!detailItem"
      :title="detailItem ? (detailItem.name + ' ' + detailItem.lastname).trim() : ''"
      :description="detailItem?.description"
      :photo="detailItem?.photo"
      :patient-group="detailItem?.patientGroup"
      @close="detailItem = null"
      @select="selectFromPopup"
    />
  </div>
</template>

